package com.ijoin.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import com.ijoin.getPlatform

/**
 * Interface for all app strings
 */
interface Strings {
    // App strings
    val appName: String

    // Main screen
    val clickMe: String
    val greetingMessage: String
    fun helloPlatform(platformName: String): String

    // Language settings
    val language: String
    val english: String
    val russian: String
    val changeLanguage: String

    // Common
    val ok: String
    val cancel: String
    val save: String
    val delete: String
    val edit: String
    val close: String
    val retry: String
    val share: String
    val unknownErrorMessage: String
    val missingEventDateError: String
    val slotSelectionRequired: String
    val userContextMissingError: String
    val cancelSlotsSelectionRequired: String
    val cancelDaySelectionRequired: String
    val restoreSlotsSelectionRequired: String
    val restoreDaySelectionRequired: String
    val cancelSlotsFailed: String
    val cancelDayFailed: String
    val restoreSlotsFailed: String
    val restoreDayFailed: String
    val cancelEventFailed: String
    val restoreEventFailed: String
    val deleteEventFailed: String
    val selectedDateUnavailableError: String
    val selectedDateInPastError: String
    val notSelected: String
    fun atTime(time: String): String
    // Notifications
    val enableNotificationsTitle: String
    val enableNotificationsBody: String
    val allowNotificationsAction: String
    val notNowAction: String

    // Home Screen
    val searchPlaceholder: String
    val myTickets: String
    val tapToViewTicket: String
    val favoriteEvents: String
    val eventsYouSaved: String
    val recommendedForYou: String
    val curatedEvents: String
    val recommendations: String
    val upcomingEvents: String
    val searchResults: String
    val noResults: String
    val noAvailableEvents: String
    val createEvent: String
    val menu: String
    val home: String
    val explore: String
    val tickets: String
    val profile: String
    val upcomingPaidTicketsTitle: String
    val otherTicketsTitle: String
    val offlineTicketsNotice: String
    val ticketsEmptyMessage: String
    val ticketDetailsTitle: String
    val ticketNumberLabel: String
    val ticketStatusActive: String
    val ticketStatusCancelled: String
    val ticketStatusRefunded: String
    val ticketStatusUsed: String
    val ticketHolderLabel: String
    val cancelTicketAction: String
    val ticketCancellationConfirmTitle: String
    val ticketCancellationConfirmMessage: String
    val ticketCancellationSuccess: String
    val ticketCancellationFailure: String
    val ticketLoadError: String
    val refresh: String
    val settings: String

    // Create Event Screen
    val createEventTitle: String
    val eventTitle: String
    val eventTitlePlaceholder: String
    val eventDescription: String
    val eventDescriptionPlaceholder: String
    val aiGenerateDescription: String
    val aiFormatDescription: String
    val addMedia: String
    val mediaDesktopLabel: String
    val mediaMobileLabel: String
    val mediaChooseFile: String
    val location: String
    val onlineEvent: String
    val onlineLink: String
    val onlineLinkPlaceholder: String
    val onlineLinkNotice: String
    val placeName: String
    val address: String
    val ticketPrice: String
    val ticketPricePlaceholder: String
    val dateAndTime: String
    val recurringEvent: String
    val recurrenceType: String
    val daysOfWeek: String
    val daysOfMonth: String
    val selectDaysOfWeek: String
    val selectDaysOfMonth: String
    val time: String
    val hiddenInfo: String
    val hiddenInfoDescription: String
    val hiddenInfoPlaceholder: String
    val enableGroupChatLabel: String
    val enableGroupChatDescription: String
    val groupChatLabel: String
    val groupChatSubtitle: String
    val carpoolSettings: String
    val enableCarpool: String
    val contactInfoSettings: String
    val requireContactInfo: String
    val requireContactInfoDescription: String
    val freeCarpool: String
    val carpoolPaymentPerPerson: String
    fun carpoolPaymentLabel(symbol: String): String
    val carpoolPaymentPlaceholder: String
    val carpoolDescription: String
    val carpoolPaymentHint: String
    val selectDate: String
    val back: String
    val create: String
    val currency: String
    val selectCurrency: String
    val category: String
    val selectCategory: String
    val tags: String
    val addTag: String
    val maxCapacity: String
    val maxCapacityPlaceholder: String
    val duration: String
    val durationMinutes: String
    fun formatDuration(minutes: Int): String
    val locationCountry: String
    val selectOnMap: String
    val locationSearchPlaceholder: String
    val onlineLinkHidden: String
    val timeZone: String
    val selectTimeZone: String
    val localTimeZone: String
    fun timeZoneValue(timeZoneId: String): String
    val mediaFilesAdded: String
    val free: String
    val priceForFreeEvent: String
    val additionalOptions: String
    val ageRestriction: String
    val languageEvent: String
    val contactInfo: String
    val website: String
    val phone: String
    val email: String
    val comment: String
    val phoneRequired: String
    val city: String
    val search: String
    val year: String
    val month: String
    val day: String
    fun weekdayShortName(index: Int): String
    fun weekdayName(index: Int): String
    fun monthNameFull(month: Int): String
    fun monthNameGenitive(month: Int): String

    // Event Wizard
    val createEventWizard: String
    val step: String
    val of: String
    val next: String
    val previous: String
    val saveDraft: String
    val editEvent: String
    val step1BasicInfo: String
    val step2Schedule: String
    val scheduleTypeLabel: String
    val scheduleOneTimeDescription: String
    val scheduleWeeklyDescription: String
    val scheduleSlotDescription: String
    val scheduleOneTimeSetup: String
    val schedulePreviewTitle: String
    fun schedulePreviewDate(date: String): String
    fun schedulePreviewTime(start: String, end: String): String
    val step3Location: String
    val eventSetupTitle: String
    val eventTypeLabel: String
    val oneTimeEvent: String
    val eventSeries: String
    val weekly: String
    val monthly: String
    val slotBooking: String
    val slotLabel: String
    val slotsLabel: String
    val selectDayOfWeek: String
    val selectDayOfMonth: String
    val availableTimeRange: String
    val startTime: String
    val endTime: String
    val endDateLabel: String
    val invalidEndDate: String
    val invalidEndTime: String
    val eventDateMustBeFuture: String
    val eventTimeMustBeFuture: String
    val slotSettings: String
    val minSlots: String
    val minSlotsPlaceholder: String
    val slotDuration: String
    val slotDurationPlaceholder: String
    val perDayTimeConfiguration: String
    val driverCompensation: String
    val fixedAmountPerPassenger: String
    val coordinateRide: String
    val participantsWithCar: String
    val needRide: String
    val draft: String
    val saved: String
    val eventSaveErrorTitle: String
    val eventSaveErrorMessage: String
    val deleteEventTitle: String
    val deleteEventMessage: String
    val eventDeletedMessage: String

    // Event Card Screen
    val organizerLabel: String
    val online: String
    val offline: String
    val eventDetailsTitle: String
    val addToFavorites: String
    val removeFromFavorites: String
    val ticketTierStandard: String
    fun ticketsAvailable(count: Int): String
    val viewTicket: String
    val bookingRequestFailed: String
    val contactSendFailed: String
    val slotLoadFailed: String
    val slotBookingFailed: String
    val ticketIssuanceFailed: String
    val unexpectedReservation: String
    val sampleReviewerName: String
    val sampleReviewTimeAgo: String
    val selectVisitDate: String
    fun availableDaysLabel(days: String): String
    val previousMonth: String
    val nextMonth: String
    fun slotSelectionMessage(date: String, time: String? = null): String
    fun eventImageContentDescription(title: String, index: Int): String
    val dateTbd: String
    val recurringWeeklyDescription: String
    val recurringMonthlyDescription: String
    // User Card Screen
    val userCardTitle: String
    val followAction: String
    val followingAction: String
    val followersCountLabel: String
    val followingCountLabel: String
    val eventsCountLabel: String
    val organizerEventsTitle: String
    val archiveSectionTitle: String
    val aboutOrganizerTitle: String
    val userEventsEmpty: String
    val userArchiveEmpty: String
    val addShortcutTitle: String
    fun addShortcutMessage(displayName: String): String
    val addShortcutConfirm: String
    val addShortcutDismiss: String
    val dateLabel: String
    val participants: String
    val spotsLeft: String
    val ticketQuantityLabel: String
    val increaseQuantity: String
    val decreaseQuantity: String
    val costLabel: String
    val serviceFee: String
    val totalAmount: String
    fun serviceFeePercent(percent: Int): String
    val dressCode: String
    val dressCodePlaceholder: String
    val description: String
    val reviews: String
    val allReviews: String
    val contactOrganizer: String
    val slotBookingsTitle: String
    val slotBookingsEmptyMessage: String
    val showAllBookings: String
    val showToday: String
    val showThisMonth: String
    val showSelectedDate: String
    val noBookingsForSelectedDate: String
    val noTicketsForSelectedDate: String
    fun totalCount(count: Int): String
    val cancelEventAction: String
    val cancelEventDescription: String
    val organizerActionsTitle: String
    val eventAlreadyCancelledNotice: String
    val eventCancelledTitle: String
    val eventCancelledSubtitle: String
    val unpublishedBookingNotice: String
    val cancellationReasonLabel: String
    val cancellationReasonPlaceholder: String
    val cancellationReasonRequired: String
    val cancelSelectedSlotsAction: String
    val cancelSelectedSlotsDescription: String
    val cancelSlotDayAction: String
    val cancelSlotDayDescription: String
    val selectedSlotsLabel: String
    val selectedDayLabel: String
    val restoreEventAction: String
    val restoreEventDescription: String
    val restoreSelectedSlotsAction: String
    val restoreSelectedSlotsDescription: String
    val restoreSlotDayAction: String
    val restoreSlotDayDescription: String
    val bookingStatusPending: String
    val bookingStatusConfirmed: String
    val bookingStatusCancelled: String
    val bookingStatusCompleted: String
    val bookingStatusNoShow: String
    val viewDetails: String
    val hideDetails: String
    val openChat: String
    val clientNotesLabel: String
    val bookingDetailsTitle: String
    val slotBookedByLabel: String
    val slotAvailableLabel: String
    val statusLabel: String
    // Stripe Connect
    val connectTitle: String
    val connectStatusActive: String
    val connectStatusPending: String
    val connectStatusPlatform: String
    val connectStatusUnderReview: String
    val connectStatusSetupNeeded: String
    val connectEnableDirectAction: String
    val connectContinueOnboarding: String
    val connectOpenDashboard: String
    val connectSwitchToPlatform: String
    val connectPlatformFeeLabel: String
    val connectDirectPaymentsEnabled: String
    val connectSwitchedToPlatform: String
    val connectOnboardingDescription: String
    val connectActiveDescription: String
    val connectPlatformDescription: String
    val connectUnderReviewDescription: String
    fun connectPlatformFeeValue(percent: Int): String

    // Billing
    val billingTitle: String
    val currentBalanceLabel: String
    val openBillingAction: String
    val withdrawFundsAction: String
    val pendingPayoutsLabel: String
    val withdrawnTotalLabel: String
    val billingFilterWeek: String
    val billingFilterMonth: String
    val billingFilterQuarter: String
    val grossSalesLabel: String
    val feesLabel: String
    val netRevenueLabel: String
    val payoutRequestsTitle: String
    val payoutRequestsEmpty: String
    val payoutStatusPending: String
    val payoutStatusProcessing: String
    val payoutStatusPaid: String
    val payoutStatusRejected: String
    val requisitesTitle: String
    val requisitesAccountLabel: String
    val requisitesInnLabel: String
    val requisitesKppLabel: String
    val requisitesBikLabel: String
    val bankLabel: String
    val soldTicketsTitle: String
    val soldTicketsEmpty: String
    val withdrawConfirmTitle: String
    fun withdrawConfirmMessage(amount: String): String
    val billingWithdrawSuccess: String
    val billingWithdrawFailed: String
    val billingWithdrawUnavailable: String
    val payoutMethodEtransfer: String
    val payoutMethodWise: String
    val payoutDetailsTitle: String
    val payoutEmailLabel: String
    val payoutWiseNameLabel: String
    val payoutWiseAccountLabel: String
    val payoutWiseBankLabel: String
    val payoutWiseRoutingLabel: String
    val payoutWiseAccountTypeLabel: String
    val payoutAccountTypeChecking: String
    val payoutAccountTypeSavings: String
    val payoutNoteLabel: String
    fun soldTicketQuantity(quantity: Int, grossAmount: String): String
    fun soldTicketBuyerInfo(buyerName: String, soldAt: String): String
    fun soldTicketServiceFee(fee: String): String
    fun shortMonthName(month: Int): String

    // Profile
    val accountDetails: String
    val interfaceLanguage: String
    val profileSavedMessage: String
    val profileLogoutDescription: String
    val profileLoadError: String
    val logoutError: String
    val profileDeletedMessage: String
    val deleteAccountTitle: String
    val deleteAccountDescription: String
    val deleteAccountAction: String
    val deleteAccountConfirmMessage: String

    // AI consent management in profile
    val aiConsentSectionTitle: String
    val aiConsentEnabled: String
    val aiConsentDisabled: String
    val aiConsentWithdrawButton: String
    val aiConsentEnableButton: String
    val aiConsentWithdrawConfirmTitle: String
    val aiConsentWithdrawConfirmMessage: String
    val aiConsentWithdrawnSuccess: String
    val aiConsentEnabledSuccess: String

    // Chat
    val messagesTitle: String
    val emptyChatList: String
    val emptyMessages: String
    val typeMessage: String
    val send: String
    val chatSearchPlaceholder: String
    val chatSearchNoResults: String
    val chatLoadError: String
    val chatSendError: String
    val chatOpenError: String
    val chatStartError: String
    val chatSearchError: String
    // Payment
    val paymentTitle: String
    val paymentSubtitle: String
    val paymentDescription: String
    val paymentPreparing: String
    val paymentFetchingTickets: String
    val paymentCompletedMessage: String
    val paymentStripePopupHint: String
    val paymentRetryButton: String
    val paymentRetryTicketsButton: String
    val paymentStartFailed: String
    val paymentTicketContextMissing: String
    val paymentTicketsNotFound: String
    val paymentTicketsFetchFailed: String
    val paymentStripeOverlayTitle: String
    val paymentStripeOverlayDescription: String
    val paymentStripeOverlayPayButton: String
    val paymentStripeOverlayPayButtonLoading: String
    val paymentStripeOverlayCancelButton: String
    val paymentStripeOverlayOpenError: String
    val paymentStripeOverlayCancelMessage: String
    val paymentStripeOverlayInitError: String
    val paymentStripeOverlayPaymentFailed: String
    val paymentStripeOverlayUnexpectedStatusPrefix: String
    val paymentStripeOverlayProcessingFailed: String

    // Backend error mapping
    val errorUserNotAuthenticated: String
    val errorAccessDenied: String
    val errorForbiddenFollowingList: String
    val errorDataUrlRequired: String
    val errorMediaStoreFailed: String
    val errorAmountMustBePositive: String
    val errorAmountExceedsBalance: String
    val errorPayoutEmailRequired: String
    val errorPayoutWiseInfoRequired: String
    val errorPayoutAccountType: String
    val errorUnsupportedPayoutMethod: String
    val errorEventNotCancelled: String
    val errorChatWithSelf: String
    val errorChatMessageEmpty: String
    val errorEventOrganizerRequired: String
    val errorEventUnavailable: String
    val errorEventFinished: String
    val errorOrganizerNotProvider: String
    val errorServiceProviderInactive: String
    val errorSlotsNotFound: String
    val errorSlotsMustSameEvent: String
    val errorSlotsMustSameOrganizer: String
    val errorSlotsUnavailable: String
    val errorSlotsInPast: String
    val errorSlotsMustBeConsecutive: String
    val errorEventIdRequired: String
    val errorQuantityAtLeastOne: String
    val errorPaidReservationRequired: String
    val errorEventOrganizerInvalid: String
    val errorEventOrganizerMissing: String
    val errorOrganizerCannotAccept: String
    val errorOccurrenceRequired: String
    val errorOccurrencePassed: String
    val errorTicketsNotEnough: String
    val errorBookingOtherUser: String
    val errorBookingNotAwaitingPayment: String
    val errorBookingCancelled: String
    val errorBookingNotActive: String
    val errorBookingIssuedTickets: String
    val errorEventRequiresPaymentFlow: String
    val errorReservationQuantityExceeded: String
    val errorTicketAlreadyValidated: String
    val errorTicketInactive: String
    val errorTicketAlreadyCancelled: String
    val errorTicketUsed: String
    val errorOccurrencesRequired: String
    val errorRequestRequired: String
    val errorBookingEventMismatch: String
    val errorBookingProcessed: String
    val errorOverrideAmountAdminOnly: String
    val errorOverrideAmountPositive: String
    val errorUnableDetermineTicketPrice: String
    val errorEmailAlreadyRegistered: String
    val errorInvalidCredentials: String
    val errorAccountInactive: String
    val errorAccountDeleted: String
    val errorInvalidRefreshToken: String
    val errorAvatarDataUrlRequired: String
    val errorAvatarMustBeImage: String
    val errorAvatarInvalidDataUrl: String
    val errorStripeSignatureInvalid: String
    val errorMediaUnsupportedFormat: String
    val errorReservationExpired: String
    val errorAccountBalanceNotZero: String

    // Authentication
    val login: String
    val register: String
    val continueWithGoogle: String
    val continueWithApple: String
    val logout: String
    val password: String
    val username: String
    val firstName: String
    val lastName: String
    val confirmPassword: String
    val alreadyHaveAccount: String
    val dontHaveAccount: String
    val loginHere: String
    val registerHere: String
    val welcomeBack: String
    val createAccount: String
    val loginError: String
    val registerError: String
    val passwordsDontMatch: String
    val fillAllFields: String
    val invalidEmail: String
    val forgotPassword: String
    val passwordResetTitle: String
    val passwordResetDescription: String
    val passwordResetSetNewPassword: String
    val sendResetLink: String
    fun passwordResetEmailSent(email: String): String
    val passwordResetTokenLabel: String
    val passwordResetTokenDescription: String
    val newPassword: String
    val resetPasswordAction: String
    val passwordResetSuccess: String
    val backToLogin: String
    val optional: String
    // Pagination / organizer panel
    val prev: String
    val showAll: String
    val collapse: String

    // Wizard validation messages
    val missingTitle: String
    val missingDescription: String
    val missingScheduleType: String
    val missingDate: String
    val missingTime: String
    val missingWeeklyDays: String
    val missingWeeklyTime: String
    val missingSlotDays: String
    val missingSlotStart: String
    val missingSlotEnd: String
    val missingLocation: String
    // Contact form
    val invalidPhone: String
    val provideAnyContact: String
    val checkContacts: String
    val contactSubmitting: String
    val contactSubmitted: String
    // Booking buttons
    val book: String
    val bookFree: String
    val bookingSuccess: String
    val bookingFailed: String
    fun bookForPrice(price: String): String

    // Organizer bookings section
    val bookingsTitle: String
    val unconfirmedRequestsTitle: String
    val detailsButton: String
    val hideDetailsBooking: String
    val noBookings: String
    val noUnconfirmedRequests: String
    val ticketInfoLabel: String
    val contactInfoLabel: String
    val paymentInfoLabel: String
    val loadingPayment: String
    val clientLabel: String
    val paymentAmount: String
    val paymentStatus: String
    val paymentDate: String

    // App Store Banner
    val appStoreBannerTitle: String
    val appStoreBannerMessage: String
    val downloadOnAppStore: String
    val getItOnGooglePlay: String
    val downloadAppLinks: String
    val appStoreUnavailableMessage: String

    // App Update
    val updateAvailable: String
    val updateRequired: String
    val updateAvailableMessage: String
    val updateRequiredMessage: String
    val updateNow: String
    val updateLater: String
    val newVersion: String
    val currentVersion: String
    val whatsNew: String

    // AI Landing
    val landingModeClassic: String
    val landingModeAi: String
    val landingModeTitle: String
    val landingDescriptionLabel: String
    val landingDescriptionPlaceholder: String
    val recordVoice: String
    val stopRecording: String
    val transcribing: String
    val recordingTooShort: String
    val recordingTooLong: String
    val microphoneAccessDenied: String
    fun recordingDuration(seconds: Int): String
    val enableLandingPageLabel: String
    val enableLandingPageDescription: String

    // Legal Documents
    val termsOfService: String
    val privacyPolicy: String
    val refundPolicy: String
    val organizerTerms: String
    val aiDataConsent: String
    val aiConsentTitle: String
    val aiConsentDescription: String
    val aiConsentDataSent: String
    val aiConsentDataSentAudio: String
    val aiConsentDataSentDescription: String
    val aiConsentDataSentLanding: String
    val aiConsentProviders: String
    val aiConsentOptional: String
    val aiConsentAccept: String
    val aiConsentDecline: String
    val iAgreeToThe: String
    val iAgreeToTerms: String
    val iAgreeToOrganizerTerms: String
    val pleaseAcceptTerms: String
    val pleaseAcceptOrganizerTerms: String
    val viewDocument: String
    val legalDocumentLoading: String
    val legalDocumentLoadError: String
    val newUserGoogleNote: String
    val legalDocumentsUpdated: String
    val legalDocumentsUpdatedDescription: String
    val iAcceptUpdatedTerms: String
    val continueButton: String
}

fun getStrings(language: AppLanguage): Strings = language.strings

object GlobalStringsProvider {
    private var currentStrings: Strings = SupportedLanguages.default.strings
    private var currentLanguageCode: String = SupportedLanguages.default.code

    fun current(): Strings = currentStrings

    fun currentCode(): String = currentLanguageCode

    fun update(strings: Strings) {
        currentStrings = strings
    }

    fun update(strings: Strings, languageCode: String) {
        currentStrings = strings
        currentLanguageCode = languageCode
    }
}

fun globalStrings(): Strings = GlobalStringsProvider.current()

fun currentLanguageCode(): String = GlobalStringsProvider.currentCode()

/**
 * Языки без поддержки шрифтов на Web (Canvas/Skia не имеет глифов)//todo
 */
private val webUnsupportedLanguages = setOf("zh", "hi", "ar")

/**
 * Проверяет, является ли текущая платформа Web
 */
private fun isWebPlatform(): Boolean = getPlatform().isWeb

/**
 * Composable to access current strings based on locale manager.
 * На Web для китайского, хинди и арабского возвращает английский интерфейс,
 * т.к. Compose Canvas не поддерживает эти шрифты.
 */
@Composable
@ReadOnlyComposable
fun strings(): Strings {
    val localeManager = LocalLocaleManager.current
    val language = localeManager.currentLanguage

    // На Web для языков без поддержки шрифтов используем английский
    return if (isWebPlatform() && language.code in webUnsupportedLanguages) {
        EnglishStrings
    } else {
        language.strings
    }
}
